<template>
  <div class="vms-workflows-page">
    <VmsPageHeader
      kicker="Approvals"
      title="Approval Workflows"
      subtitle="Two workflows only: Head Office and Branch/Centre. Steps are roles — people are assigned per location in Approval Groups."
      icon="mdi-clipboard-flow-outline"
    >
      <template #actions>
        <VmsColumnVisibility
          v-model="selectedHeaders"
          :headers="headers"
          @change="toggleColumnVisibility"
        />
        <v-text-field
          label="Search..."
          v-model="filterReportBySearch"
          append-icon="mdi-magnify"
          dense
          outlined
          hide-details
          clearable
          color="primary"
          class="vms-toolbar-search"
        ></v-text-field>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              dark
              class="text-capitalize"
              :loading="loading"
              @click="goToOrganizations"
              elevation="0"
              fab
              x-small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-format-list-bulleted-square</v-icon>
            </v-btn>
          </template>
          <span>Back to Organizations</span>
        </v-tooltip>
        <v-btn color="primary" dark class="text-capitalize vms-primary-cta" @click="openCreate">
          <v-icon left>mdi-plus-thick</v-icon> New
        </v-btn>
      </template>
    </VmsPageHeader>

    <VmsResponsiveList :items="items" :loading="loading" loading-text="Loading workflows…">
      <template #table>
        <v-data-table
          :headers="computedHeaders"
          :items="items"
          sort-by="title"
          class="elevation-0 vms-surface"
          :loading="loading"
          loading-text="Loading... Please wait"
          :search="filterReportBySearch"
          :custom-filter="tableSearchFilter"
          show-select
          :single-select="singleSelect"
          v-model="selected"
          :server-items-length="pagination?.total"
          :options.sync="options"
        >
          <template v-slot:[`item.approval_groups`]="{ item }">
            <div v-if="(item.steps && item.steps.length) || (item.approval_groups && item.approval_groups.length)">
              <span
                v-for="(step, i) in (item.steps || item.approval_groups)"
                :key="`wf-step-${item.id}-${step.role_key || step.id}-${i}`"
              >
                <span v-if="i != 0"> <v-icon small>mdi-arrow-right-bold</v-icon> </span>
                <span class="primary--text">{{ step.title || step.label }}</span>
              </span>
            </div>
            <div v-else>
              <a class="red--text" @click="editItem(item)">
                Please configure workflow steps
              </a>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="vms-table-actions">
              <VmsIconAction
                v-if="approvalStatus(item)"
                icon="mdi-order-alphabetical-ascending"
                label="Reorder steps"
                @click="changeWorkflowSteps(item)"
              />
              <VmsIconAction
                icon="mdi-pencil-outline"
                label="Edit"
                @click="editItem(item)"
              />
              <VmsIconAction
                icon="mdi-delete-outline"
                label="Delete"
                color="error"
                @click="deleteItem(item)"
              />
            </div>
          </template>

          <template v-slot:no-data>
            <div class="py-10 text-center">
              <v-icon large color="grey lighten-1">mdi-clipboard-flow-outline</v-icon>
              <p class="mt-2 grey--text px-4">
                No workflows yet. Create one Head Office workflow and one Branch/Centre workflow. Steps are roles; assign people per branch in Approval Groups.
              </p>
            </div>
          </template>
        </v-data-table>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">{{ item.title }}</div>
        <div class="vms-list-card__meta">
          <v-chip small :color="item.is_parent_workflow ? 'primary' : 'info'" dark>
            {{ item.is_parent_workflow ? 'HQ' : 'Branch' }}
          </v-chip>
        </div>
        <div class="vms-list-card__row" v-if="(item.steps && item.steps.length) || (item.approval_groups && item.approval_groups.length)">
          <span
            v-for="(step, i) in (item.steps || item.approval_groups)"
            :key="`wf-card-step-${item.id}-${step.role_key || step.id}-${i}`"
          >
            <span v-if="i != 0"> <v-icon x-small>mdi-arrow-right-bold</v-icon> </span>
            <span class="primary--text">{{ step.title || step.label }}</span>
          </span>
        </div>
        <div class="vms-list-card__row" v-else>
          <a class="red--text" @click="editItem(item)">Please configure workflow steps</a>
        </div>
        <div class="vms-list-card__actions">
          <VmsIconAction
            v-if="approvalStatus(item)"
            icon="mdi-order-alphabetical-ascending"
            label="Reorder steps"
            @click="changeWorkflowSteps(item)"
          />
          <VmsIconAction
            icon="mdi-pencil-outline"
            label="Edit"
            @click="editItem(item)"
          />
          <VmsIconAction
            icon="mdi-delete-outline"
            label="Delete"
            color="error"
            @click="deleteItem(item)"
          />
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <v-icon large color="grey lighten-1">mdi-clipboard-flow-outline</v-icon>
          <p class="mt-2 grey--text px-4">
            No workflows yet. Create one Head Office workflow and one Branch/Centre workflow. Steps are roles; assign people per branch in Approval Groups.
          </p>
        </div>
      </template>
    </VmsResponsiveList>

    <v-dialog v-model="dialog" max-width="560px">
      <v-card>
        <v-form class="vms-form" ref="form" v-model="valid" lazy-validation>
          <v-card-title class="vms-form-title">
            {{ formTitle }}
          </v-card-title>
          <v-card-subtitle class="vms-form-subtitle">
            Define the approval path once. For Branch workflows, each location uses its own groups for these roles.
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text class="vms-form-card">
            <div class="vms-form-section">
              <span class="vms-form-section__label">Workflow details</span>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.title"
                    label="Name"
                    :rules="rules.required"
                    dense
                    hide-details="auto"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.description"
                    outlined
                    label="Description"
                    dense
                    hide-details="auto"
                    :rules="rules.required"
                    rows="2"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
            <div class="vms-form-section">
              <span class="vms-form-section__label">Applies to</span>
              <v-switch
                v-model="editedItem.is_parent_workflow"
                inset
                :label="editedItem?.is_parent_workflow ? 'Head Office (HQ)' : 'All Branches / Centres'"
                hide-details
                class="mt-0 pt-0"
                color="primary"
                @change="applyDefaultSteps"
              ></v-switch>
            </div>
            <div class="vms-form-section">
              <span class="vms-form-section__label">Approval path (roles in order)</span>
              <p class="text-caption grey--text mb-2">
                {{ editedItem.is_parent_workflow
                  ? 'HQ visits: Governor’s Office → AD Security → HQ Security clearance'
                  : 'Branch visits: Manager → Assistant Administrator → Branch Security clearance' }}
              </p>
              <v-select
                v-model="editedItem.steps"
                :items="availableRoles"
                item-text="label"
                item-value="role_key"
                label="Roles in this workflow"
                multiple
                chips
                outlined
                dense
                hide-details="auto"
                :rules="rules.select2"
                return-object
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip small class="ma-1">
                    {{ index + 1 }}. {{ item.title || item.short || item.label || item.role_key }}
                  </v-chip>
                </template>
              </v-select>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="vms-form-actions">
            <v-btn text color="grey darken-1" class="text-capitalize" @click="close">
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              class="text-capitalize white--text"
              :loading="loading"
              @click="save"
            >
              Submit
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <div class="text-center">
      <v-dialog v-model="dialogChangeSteps" width="600">
        <v-card>
          <v-form ref="changeStepsForm" v-model="valid" lazy-validation>
            <v-card-title>
              <span class="headline error--text text--darken-1"
                >Change Workflow Steps</span
              >
            </v-card-title>
            <v-divider></v-divider>

            <v-card-subtitle>
              <span class="headline grey--text text--darken-1">{{
                editedItem.title
              }}</span>
            </v-card-subtitle>
            <v-divider></v-divider>

            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="12" md="12" class="my-n2">
                    <v-list two-line subheader>
                      <v-subheader>Hold and Drag To Reorder Roles</v-subheader>

                      <v-list-item>
                        <draggable
                          v-model="editedItem.steps"
                          group="workflow"
                          @start="drag = true"
                          @end="drag = false"
                        >
                          <v-list-item-content
                            v-for="step in editedItem.steps"
                            :key="step.role_key"
                          >
                            <v-chip color="success" outlined>
                              <v-avatar left>
                                <v-icon>mdi-bullseye</v-icon>
                              </v-avatar>
                              {{ step.title || step.label || step.role_key }}
                            </v-chip>
                          </v-list-item-content>
                        </draggable>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <v-col cols="12" sm="12" md="12" class="mt-1">
                    <v-list subheader>
                      <v-subheader>Minimum Approvers Per Step</v-subheader>

                      <v-list-item
                        v-for="step in editedItem.steps"
                        :key="'input-' + step.role_key"
                      >
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="step.title || step.label || step.role_key"
                          ></v-list-item-title>
                        </v-list-item-content>

                        <v-list-item-icon>
                          <v-text-field
                            v-model="step.min_approvers"
                            label="Approvers"
                            :rules="rules.approvers"
                            color="grey"
                            dense
                            outlined
                            class="mb-n3"
                            type="number"
                          >
                            <template v-slot:label>
                              <span class="input__label"
                                >Approvers
                                <v-icon small color="error" class="mt-n2"
                                  >mdi-star-outline</v-icon
                                ></span
                              >
                            </template>
                          </v-text-field>
                        </v-list-item-icon>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                class="text-capitalize mx-1"
                dark
                @click="dialogChangeSteps = false"
              >
                Close <v-icon small>mdi-close</v-icon>
              </v-btn>
              <v-btn
                color="success"
                dark
                class="text-capitalize mx-1"
                :loading="loading"
                @click="updateWorkflowSteps(editedItem)"
              >
                Change <v-icon>mdi-radar</v-icon>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>
<script>
import WorkflowService from "../../services/workflow.service";
import draggable from "vuedraggable";
import Swal from "sweetalert2";
import VmsPageHeader from "../../components/VmsPageHeader.vue";
import VmsResponsiveList from "../../components/VmsResponsiveList.vue";
import VmsIconAction from "../../components/VmsIconAction.vue";
import VmsColumnVisibility from "../../components/VmsColumnVisibility.vue";
import columnVisibility from "../../mixins/columnVisibility";

export default {
  components: {
    draggable,
    VmsPageHeader,
    VmsResponsiveList,
    VmsIconAction,
    VmsColumnVisibility,
  },
  mixins: [columnVisibility],
  data: () => ({
    name: "ApprovalGroup",
    availableRoles: [],
    hqRoles: [],
    branchRoles: [],
    search: "",
    loading: true,
    items: [],
    editedIndex: -1,
    editedItem: {
      title: "",
      description: "",
      steps: [],
      is_parent_workflow: false,
    },
    defaultItem: {
      title: "",
      description: "",
      steps: [],
      is_parent_workflow: false,
    },
    remember: 0,
    timeout: 2000,
    dialog: false,
    dialogChangeSteps: false,
    valid: true,
    rules: {
      select: [(v) => !!v || "An item should be selected"],
      select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
      required: [
        (v) => !!v || "Field is required",
        (v) => (v && v.length >= 3) || "Min 3 characters",
      ],
      file: [
        (v) => !!v || "File is required",
        (v) => (v && v.length > 0) || "File is required",
      ],
      approvers: [(v) => !!v || "Field is required"],
    },
    workflow_steps: [
      {
        id: 1,
        name: "One",
      },
      {
        id: 1,
        name: "Two",
      },
      {
        id: 1,
        name: "Three",
      },
      {
        id: 1,
        name: "Four",
      },
      {
        id: 1,
        name: "Five",
      },
    ],
    selectRules: [(v) => !!v || "Select is required"],
    headers: [
      {
        text: "#",
        align: "start",
        filterable: true,
        value: "id",
        visible: true,
      },
      {
        text: "Title",
        value: "title",
        visible: true,
      },
      {
        text: "Description",
        value: "description",
        visible: false,
      },
      {
        text: "Workflow Steps",
        value: "approval_groups",
        visible: true,
      },
      {
        text: "Actions",
        align: "center",
        value: "actions",
        sortable: false,
        visible: true,
        sticky: true,
      },
    ],
    filterReportBySearch: null,
    searchDebounce: null,
    search_term: null,
    options: {},
    pagination: {},
    singleSelect: false,
    selected: [],
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New" : "Edit";
    },
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    getLoading() {
      return this.loading;
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    options: {
      handler () {
        this.index();
      },
      deep: true,
    },
    filterReportBySearch(val) {
      clearTimeout(this.searchDebounce);

      this.searchDebounce = setTimeout(() => {
        this.search_term = (val || '').trim();

        this.options = {
          ...this.options,
          page: 1,
        };

        this.index();
      }, 300);
    },
  },
  created() {
    this.initColumnVisibility();
    this.loadRoles().then(() => {
      this.index();
    });
  },
  methods: {
    openCreate() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem, { steps: [] });
      this.applyDefaultSteps();
      this.dialog = true;
    },
    goToOrganizations() {
      this.$router.push("organisations");
    },
    loadRoles() {
      return Promise.all([
        WorkflowService.getApprovalRoles('hq'),
        WorkflowService.getApprovalRoles('branch'),
      ]).then(([hqRes, branchRes]) => {
        this.hqRoles = hqRes?.data?.data || [];
        this.branchRoles = branchRes?.data?.data || [];
        this.availableRoles = this.editedItem.is_parent_workflow
          ? this.hqRoles
          : this.branchRoles;
      }).catch((error) => {
        console.log(error);
      });
    },
    applyDefaultSteps() {
      this.availableRoles = this.editedItem.is_parent_workflow
        ? this.hqRoles
        : this.branchRoles;
      this.editedItem.steps = (this.availableRoles || []).map((role) => ({
        role_key: role.role_key,
        title: role.short || role.label,
        label: role.label,
        min_approvers: 1,
        approvers: 1,
      }));
    },
    buildStepsPayload() {
      return (this.editedItem.steps || []).map((step, index) => ({
        role_key: step.role_key || step.id,
        order: index,
        min_approvers: Number(step.min_approvers || step.approvers || 1),
      }));
    },
    changeWorkflowSteps(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = JSON.parse(JSON.stringify(item));
      this.editedItem.steps = (item.steps || item.approval_groups || []).map((step) => ({
        ...step,
        role_key: step.role_key || step.id,
        title: step.title || step.short || step.label,
        label: step.label || step.title,
        min_approvers: step.min_approvers || step.approvers || 1,
      }));
      this.dialogChangeSteps = true;
      
      this.$nextTick(() => {
        if (this.$refs.changeStepsForm) {
          this.$refs.changeStepsForm.resetValidation();
        }
      });
    },
    updateWorkflowSteps(item) {
      if (this.$refs.changeStepsForm.validate()) {
        this.loading = true;
        const payload = {
          id: item.id,
          steps: this.buildStepsPayload(),
          approval_groups: this.buildStepsPayload(),
        };
        return WorkflowService.updateWorkflowSteps(payload).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", response.data.message);
              this.index();
              this.loading = false;
              this.close();
            } else {
              this.loading = false;
              console.log(response.data.error);
            }
          },
          (error) => {
            this.loading = false;
            console.log(error);
          }
        );
      }
    },
    filterText(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value.toString().toLocaleLowerCase().indexOf(search) !== -1
      );
    },
    getNestedValue(obj, path) {
      if (!path) return undefined;
      return String(path).split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    },
    tableSearchFilter(value, search, item) {
      if (!search) return true;
      const term = String(search).toLowerCase().trim();

      return this.headers.some((header) => {
        const v = this.getNestedValue(item, header.value);
        return v !== null && v !== undefined && String(v).toLowerCase().includes(term);
      });
    },
    approvalStatus(item) {
      if (item.approved == 1) {
        return false;
      }

      return true;
    },
    handleLogout() {
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },
    validate() {
      this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    index() {
      return WorkflowService.index({
        options: this.options,
        search: this.search_term ?? this.filterReportBySearch,
      }).then(
        (response) => {
          if (response.status == 200) {
            this.items = response?.data?.data?.data;
            this.pagination = response?.data?.data;
            this.loading = false;
          } else {
            this.items = [];
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.availableRoles = item.is_parent_workflow ? this.hqRoles : this.branchRoles;
      this.editedItem.steps = (item.steps || item.approval_groups || []).map((step) => ({
        role_key: step.role_key || step.id,
        title: step.title || step.short || step.label,
        label: step.label || step.title,
        min_approvers: step.min_approvers || step.approvers || 1,
        approvers: step.min_approvers || step.approvers || 1,
      }));
      this.dialog = true;
    },
    deleteItem(item) {
      const index = this.items.indexOf(item);
      Swal.fire({
        title: "Are you sure you want to delete this workflow?",
        text: "You cannot undo this action",
        icon: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed && this.items.splice(index, 1)) {
          WorkflowService.delete(item).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch("alert/success", response.data.message);
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
              }
              if (error.response.status == 401) {
                this.handleLogout();
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        }
      });
    },

    close() {
      this.dialog = false;
      this.dialogChangeSteps = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    save() {
      this.loading = true;
      this.$refs.form.validate();
      if (this.valid) {
        const payload = {
          ...this.editedItem,
          steps: this.buildStepsPayload(),
        };
        // edit
        if (this.editedIndex > -1) {
          payload._method = 'PUT';
          WorkflowService.update(payload).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch("alert/success", response.data.message);
                this.loading = false;
                this.index();
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.index();
                this.loading = false;
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
                this.loading = false;
                this.index();
              }
              if (error.response.status == 401) {
                this.handleLogout();
                this.loading = false;
                this.index();
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        } else {
          // create
          WorkflowService.store(payload).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch(
                  "alert/success",
                  "Item created successfully"
                );
                this.loading = false;
                this.index();
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.index();
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
                this.loading = false;
                this.index();
              }
              if (error.response.status == 401) {
                this.handleLogout();
                this.loading = false;
                this.index();
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        }
      } else {
        this.loading = false;
        this.index();
      }
    },
  },
};
</script>
